<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { useDebounceFn } from '@vueuse/core';
import { useDropdownState } from 'src/composables/useDropdownState';
import SharedDropdown from 'src/components/shared/SharedDropdown.vue';
import { useEntitySearch } from 'src/composables/ticket-entities/useEntitySearch';
import type {
  BaseEntity,
  EntityMismatch,
  EntitySearchConfig,
  DisplayProperty,
} from 'src/composables/ticket-entities/types';
import type { EntityPanelTheme } from 'src/components/ticket-edit/entity-display';

const props = defineProps<{
  label: string;
  theme: EntityPanelTheme;
  config: EntitySearchConfig<any>;
  modelValue?: BaseEntity;
  parentId?: string;
  parentName?: string;
  mismatch?: EntityMismatch;
  disabled?: boolean;
  loading?: boolean;
  invalid?: boolean;
}>();

const emit = defineEmits<{
  select: [entity: BaseEntity];
  clear: [];
}>();

const searchQuery = ref('');
const panelEl = ref<HTMLElement>();
const { isOpen: showDropdown } = useDropdownState(panelEl);
const parentIdRef = computed(() => props.parentId);
const parentNameRef = computed(() => props.parentName);

const {
  groupedItems,
  apiSearchUnrelated,
  isLoading,
  isLoadingMore,
  hasMore,
  loadItems,
  loadMoreItems,
} = useEntitySearch({
  config: props.config,
  searchQuery,
  parentId: parentIdRef,
  parentName: parentNameRef,
});

const apiResults = ref<any[]>([]);
const apiSearchPage = ref(1);
const apiSearchHasMore = ref(false);
const apiSearchLoading = ref(false);
const apiSearchQuery = ref('');

const debouncedApiSearch = useDebounceFn(async (query: string) => {
  if (!query.trim() || !props.config.apiSearch) {
    apiResults.value = [];
    apiSearchPage.value = 1;
    apiSearchHasMore.value = false;
    apiSearchQuery.value = '';
    return;
  }
  try {
    apiSearchPage.value = 1;
    apiSearchQuery.value = query;
    const response = await apiSearchUnrelated(query, { page: 1, limit: 20 });
    apiResults.value = response.data;
    apiSearchHasMore.value = response.hasMore;
  } catch {
    apiResults.value = [];
    apiSearchHasMore.value = false;
  }
}, 300);

async function loadMoreApiSearchResults(): Promise<void> {
  if (apiSearchLoading.value || !apiSearchHasMore.value || !apiSearchQuery.value) return;
  apiSearchLoading.value = true;
  try {
    apiSearchPage.value += 1;
    const response = await apiSearchUnrelated(apiSearchQuery.value, { page: apiSearchPage.value, limit: 20 });
    apiResults.value = [...apiResults.value, ...response.data];
    apiSearchHasMore.value = response.hasMore;
  } catch {
    apiSearchPage.value -= 1;
  } finally {
    apiSearchLoading.value = false;
  }
}

watch(searchQuery, (q) => debouncedApiSearch(q));

const hasLoadedItems = ref(false);

function handleFocus(): void {
  if (!hasLoadedItems.value) {
    hasLoadedItems.value = true;
    loadItems();
  }
  showDropdown.value = true;
}

function handleBlur(): void {
  setTimeout(() => {
    showDropdown.value = false;
  }, 200);
}

function handleDropdownScroll(event: Event): void {
  const el = event.target as HTMLElement;
  const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
  if (!nearBottom) return;

  if (searchQuery.value) {
    loadMoreApiSearchResults();
  } else {
    if (hasMore.value && !isLoadingMore.value) {
      loadMoreItems();
    }
  }
}

function getItemSearchText(item: BaseEntity): string {
  return [
    props.config.getDisplayName(item),
    ...props.config.getDisplayProps(item).map((p: DisplayProperty) => p.value),
  ].join(' ').toLowerCase();
}

const filteredGroups = computed(() => {
  const q = searchQuery.value.toLowerCase().trim();
  const groups = groupedItems.value;

  if (!q) return groups;

  const result: typeof groups = [];
  const hasRelatedGroup = !!props.parentId && groups.length >= 2;

  if (hasRelatedGroup) {
    const relatedGroup = groups[0];
    const filtered = relatedGroup.items.filter((item) => getItemSearchText(item).includes(q));
    if (filtered.length > 0) {
      result.push({ header: relatedGroup.header, items: filtered });
    }
  }

  if (apiResults.value.length > 0) {
    const relatedIds = hasRelatedGroup
      ? new Set(groups[0].items.map((i: any) => i.id))
      : new Set<string>();
    const unrelated = apiResults.value.filter((item: any) => !relatedIds.has(item.id));
    if (unrelated.length > 0) {
      const otherHeader = hasRelatedGroup && groups[1] ? groups[1].header : `Other ${props.label}s`;
      result.push({ header: otherHeader, items: unrelated });
    }
  }

  return result;
});

const hasResults = computed(() => filteredGroups.value.some((g) => g.items.length > 0));

function handleSelect(entity: BaseEntity): void {
  emit('select', entity);
  searchQuery.value = '';
  showDropdown.value = false;
}

function handleClear(): void {
  emit('clear');
  searchQuery.value = '';
}

const selectedName = computed(() =>
  props.modelValue ? props.config.getDisplayName(props.modelValue) : null,
);

const selectedProps = computed<DisplayProperty[]>(() =>
  props.modelValue ? props.config.getDisplayProps(props.modelValue) : [],
);

const copiedValue = ref<string | null>(null);

function copyToClipboard(value: string): void {
  navigator.clipboard.writeText(value);
  copiedValue.value = value;
  setTimeout(() => (copiedValue.value = null), 1500);
}

function getItemName(item: BaseEntity): string {
  return props.config.getDisplayName(item);
}

function getItemSubtitle(item: BaseEntity): string {
  return props.config.getDisplayProps(item).map((p: DisplayProperty) => p.value).join(' · ');
}

function isSelected(item: BaseEntity): boolean {
  return (item as any).id === (props.modelValue as any)?.id;
}
</script>

<template>
  <div ref="panelEl" class="entity-panel" :class="{ 'entity-panel-invalid': invalid }">
    <!-- Row 1: Badge + clear -->
    <div class="flex items-center justify-between mb-2">
      <div :class="['entity-badge', theme.badge]">
        <i :class="['pi', config.icon]" style="font-size: 11px" />
        {{ label }}
      </div>
      <div class="flex items-center gap-1">
        <slot name="actions" />
        <button v-if="modelValue && !disabled" class="entity-clear" @click="handleClear">
          <i class="pi pi-times" style="font-size: 9px" />
        </button>
      </div>
    </div>

    <!-- Loading state -->
    <div v-if="loading && !modelValue" class="entity-loading">
      <i class="pi pi-spinner pi-spin" style="font-size: 12px" />
      <span>Loading {{ label.toLowerCase() }}...</span>
    </div>

    <!-- Row 2: Search input -->
    <div v-else class="relative mb-2">
      <div class="entity-search-field" :class="{ 'entity-search-field-focus': showDropdown }">
        <i class="pi pi-search text-surface-400 shrink-0" style="font-size: 12px" />
        <input
          type="text"
          v-model="searchQuery"
          :placeholder="modelValue ? `Search to change ${label.toLowerCase()}...` : `Search ${label.toLowerCase()}...`"
          class="entity-search-input"
          :disabled="disabled"
          @focus="handleFocus"
          @blur="handleBlur"
        />
        <i v-if="isLoading" class="pi pi-spinner pi-spin text-surface-400 shrink-0" style="font-size: 12px" />
      </div>

      <!-- Grouped dropdown -->
      <SharedDropdown :open="showDropdown" @scroll="handleDropdownScroll">
        <template v-if="!hasResults && !isLoading">
          <div class="px-3 py-4 text-center text-xs text-surface-400">No results found</div>
        </template>
        <template v-else>
          <template v-for="(group, gi) in filteredGroups" :key="gi">
            <div v-if="group.header" class="entity-dropdown-header">
              <span v-if="gi === 0 && parentId" class="inline-block w-1.5 h-1.5 rounded-full bg-blue-400 mr-1.5" />
              {{ group.header }}
            </div>
            <button
              v-for="item in group.items"
              :key="(item as any).id"
              class="te-dropdown-item"
              :class="{
                'te-dropdown-item-active': isSelected(item),
                'te-dropdown-item-related': gi === 0 && parentId && !isSelected(item),
              }"
              @mousedown.prevent="handleSelect(item)"
            >
              <div :class="['w-7 h-7 rounded-md flex items-center justify-center shrink-0', theme.bg]">
                <i :class="['pi', config.icon, theme.icon]" style="font-size: 11px" />
              </div>
              <div class="min-w-0 flex-1">
                <div class="text-xs font-medium text-surface-800 dark:text-surface-200 truncate">
                  {{ getItemName(item) }}
                </div>
                <div class="text-[11px] text-surface-400 truncate">
                  {{ getItemSubtitle(item) }}
                </div>
              </div>
              <i v-if="isSelected(item)" class="pi pi-check text-primary-500 shrink-0" style="font-size: 11px" />
            </button>
          </template>
          <div v-if="isLoadingMore || apiSearchLoading" class="flex justify-center py-2">
            <i class="pi pi-spinner pi-spin text-surface-400" style="font-size: 12px" />
          </div>
        </template>
      </SharedDropdown>
    </div>

    <!-- Row 3: Selected entity details -->
    <template v-if="modelValue">
      <div class="entity-name">
        {{ selectedName }}
      </div>
      <div v-if="mismatch" class="mismatch-banner">
        <i class="pi pi-exclamation-triangle" style="font-size: 11px" />
        Belongs to {{ mismatch.belongsToOrganisation.name }}
      </div>
      <div class="entity-props">
        <template v-for="p in selectedProps" :key="p.key">
          <span class="entity-prop-key">{{ p.key }}</span>
          <span
            :class="['entity-prop-val', { copiable: p.copyable }]"
            @click="p.copyable && copyToClipboard(p.value)"
            :title="p.copyable ? (copiedValue === p.value ? 'Copied!' : 'Click to copy') : undefined"
          >
            <template v-if="copiedValue === p.value">
              <i class="pi pi-check text-green-500" style="font-size: 10px" /> Copied
            </template>
            <template v-else>{{ p.value }}</template>
          </span>
        </template>
      </div>
    </template>
  </div>
</template>

<style scoped>
.entity-panel {
  padding: 0.625rem 0.75rem;
  border-radius: 0.5rem;
  border: 1px solid var(--p-surface-200);
  background: white;
}
:root[class*='app-dark'] .entity-panel {
  border-color: var(--p-surface-700);
  background: var(--p-surface-800);
}
.entity-panel-invalid {
  border-color: var(--p-red-400) !important;
}

.entity-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.3125rem;
  padding: 0.1875rem 0.5rem;
  border-radius: 0.25rem;
  font-size: 0.75rem;
  font-weight: 600;
}

.entity-loading {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.4375rem 0.5rem;
  margin-bottom: 0.5rem;
  font-size: 0.75rem;
  color: var(--p-surface-400);
}

.entity-clear {
  width: 1.375rem;
  height: 1.375rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.25rem;
  color: var(--p-surface-400);
  transition: all 0.15s;
}
.entity-clear:hover {
  color: var(--p-red-500);
  background: var(--p-red-50);
}
:root[class*='app-dark'] .entity-clear:hover {
  background: color-mix(in srgb, var(--p-red-500) 15%, transparent);
}

.entity-search-field {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.3125rem 0.5rem;
  border-radius: 0.375rem;
  border: 1px solid var(--p-surface-200);
  background: var(--p-surface-50);
  transition: all 0.15s;
}
:root[class*='app-dark'] .entity-search-field {
  border-color: var(--p-surface-700);
  background: var(--p-surface-900);
}
.entity-search-field:hover {
  border-color: var(--p-surface-300);
}
:root[class*='app-dark'] .entity-search-field:hover {
  border-color: var(--p-surface-600);
}
.entity-search-field-focus {
  border-color: var(--p-primary-400);
  box-shadow: 0 0 0 2px color-mix(in srgb, var(--p-primary-500) 10%, transparent);
}
:root[class*='app-dark'] .entity-search-field-focus {
  border-color: var(--p-primary-600);
}

.entity-search-input {
  flex: 1;
  padding: 0;
  font-size: 0.875rem;
  color: var(--p-surface-700);
  background: transparent;
  border: none;
  outline: none;
  width: 100%;
  min-width: 0;
}
:root[class*='app-dark'] .entity-search-input {
  color: var(--p-surface-300);
}
.entity-search-input::placeholder {
  color: var(--p-surface-400);
  font-size: 0.875rem;
}

.entity-name {
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--p-surface-800);
  margin-bottom: 0.25rem;
  display: flex;
  align-items: center;
}
:root[class*='app-dark'] .entity-name {
  color: var(--p-surface-100);
}

.mismatch-banner {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.25rem 0.5rem;
  margin-bottom: 0.375rem;
  border-radius: 0.25rem;
  font-size: 0.75rem;
  font-weight: 500;
  color: var(--p-amber-700);
  background: var(--p-amber-50);
  border: 1px solid var(--p-amber-200);
}
:root[class*='app-dark'] .mismatch-banner {
  color: var(--p-amber-400);
  background: color-mix(in srgb, var(--p-amber-500) 10%, transparent);
  border-color: color-mix(in srgb, var(--p-amber-500) 25%, transparent);
}

.entity-props {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 0.125rem 0.625rem;
  font-size: 0.875rem;
  line-height: 1.6;
}
.entity-prop-key {
  color: var(--p-surface-400);
  white-space: nowrap;
  text-align: left;
}
.entity-prop-val {
  color: var(--p-surface-700);
  user-select: text;
}
:root[class*='app-dark'] .entity-prop-val {
  color: var(--p-surface-300);
}
.entity-prop-val.copiable {
  cursor: copy;
  border-bottom: 1px dashed var(--p-surface-300);
  transition: all 0.15s;
}
:root[class*='app-dark'] .entity-prop-val.copiable {
  border-bottom-color: var(--p-surface-600);
}
.entity-prop-val.copiable:hover {
  color: var(--p-primary-600);
  border-bottom-color: var(--p-primary-400);
}
:root[class*='app-dark'] .entity-prop-val.copiable:hover {
  color: var(--p-primary-400);
}

.entity-dropdown-header {
  display: flex;
  align-items: center;
  padding: 0.375rem 0.625rem;
  margin-top: 0.125rem;
  font-size: 0.8125rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: var(--p-surface-500);
  user-select: none;
}
:root[class*='app-dark'] .entity-dropdown-header {
  color: var(--p-surface-400);
}
.entity-dropdown-header:first-child {
  margin-top: 0;
}

.te-dropdown-item {
  display: flex;
  align-items: center;
  width: 100%;
  padding: 0.4375rem 0.625rem;
  gap: 0.625rem;
  text-align: left;
  cursor: pointer;
  transition: background 0.1s;
}
.te-dropdown-item:hover {
  background: var(--p-surface-100);
}
:root[class*='app-dark'] .te-dropdown-item:hover {
  background: var(--p-surface-700);
}
.te-dropdown-item-active {
  background: color-mix(in srgb, var(--p-primary-500) 8%, transparent);
}
:root[class*='app-dark'] .te-dropdown-item-active {
  background: color-mix(in srgb, var(--p-primary-500) 12%, transparent);
}
.te-dropdown-item-related {
  background: color-mix(in srgb, var(--p-blue-500) 4%, transparent);
}
:root[class*='app-dark'] .te-dropdown-item-related {
  background: color-mix(in srgb, var(--p-blue-500) 8%, transparent);
}
.te-dropdown-item-related:hover {
  background: color-mix(in srgb, var(--p-blue-500) 10%, transparent);
}
:root[class*='app-dark'] .te-dropdown-item-related:hover {
  background: color-mix(in srgb, var(--p-blue-500) 15%, transparent);
}
</style>
